/**
 * Client-side checkout validation — BUILD-PLAN §24, §41.
 *
 * Two rules govern everything here.
 *
 * First, this is guidance, never enforcement. The server re-validates every
 * field (§29); if these two ever disagree, the server wins. What this buys is
 * that the customer is corrected at the field they are in, in the vocabulary
 * of their own country, instead of after a round trip that scrolls them to
 * the top of a long form.
 *
 * Second, a message only appears once the customer has had their turn — after
 * they leave a field, or when they try to submit. A form that turns red as
 * soon as it renders is telling the customer they are wrong before they have
 * done anything.
 */
import { rulesFor } from '@/lib/addressRules';
import type { AddressInput } from '@/types/order';
import type { CountryRule } from '@/types/platform';

export interface CheckoutValues {
  email: string;
  phone: string;
  address: AddressInput;
  method: string;
  scheduledDate: string;
  acceptsTerms: boolean;
}

/** Keyed by the same field paths the API returns, so the two interleave. */
export type Errors = Record<string, string>;

/*
 | Deliberately not the "full" RFC 5322 grammar. That pattern is famously
 | unreadable and still cannot tell you whether an address receives mail. This
 | catches the mistakes people actually make — a missing @, a trailing dot, a
 | space — and leaves the rest to the verification email.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

/** Matches the server's `regex:/^[0-9+()\-\s]+$/` on PlaceOrderRequest. */
const PHONE_CHARS = /^[0-9+()\-\s]+$/;

/**
 * Today's date in the CUSTOMER's timezone, as YYYY-MM-DD.
 *
 * Not `toISOString().slice(0, 10)`, which is the classic trap: it converts to
 * UTC first, so anyone east of Greenwich late in the evening gets tomorrow's
 * date, and anyone west of it early in the morning gets yesterday's. A date
 * picker whose minimum is a day out lets the customer choose a delivery date
 * the server then rejects.
 */
export function localDate(offsetDays = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Digits only, so formatting characters do not count toward the length. */
const digitCount = (value: string) => (value.match(/\d/g) ?? []).length;

/**
 * @param countries The server's registry. Passed in rather than imported so
 *                  this stays a pure function — testable without a running
 *                  API, and incapable of drifting from what the form shows.
 */
export function validateCheckout(values: CheckoutValues, countries: CountryRule[] = []): Errors {
  const errors: Errors = {};
  const { email, phone, address, method, scheduledDate, acceptsTerms } = values;
  const rules = rulesFor(address.country_code, countries);

  /* ------------------------------------------------------------- contact */
  if (!email.trim()) {
    errors['email'] = 'Please enter your email address.';
  } else if (!EMAIL.test(email.trim())) {
    errors['email'] = 'Please enter a valid email address, like name@example.com.';
  }

  /*
   | Phone is optional, but a half-typed one is worse than none: it looks like
   | a contact route and is not one.
   */
  if (phone.trim()) {
    if (!PHONE_CHARS.test(phone)) {
      errors['phone'] = 'Please use digits, spaces and + ( ) - only.';
    } else if (digitCount(phone) < 7) {
      errors['phone'] = 'That looks too short for a phone number.';
    } else if (digitCount(phone) > 15) {
      // E.164 caps the subscriber number at 15 digits.
      errors['phone'] = 'That looks too long for a phone number.';
    }
  }

  /* ------------------------------------------------------------- address */
  if (!address.first_name?.trim()) errors['shipping_address.first_name'] = 'Please enter a first name.';
  if (!address.last_name?.trim()) errors['shipping_address.last_name'] = 'Please enter a last name.';
  if (!address.line_1?.trim()) errors['shipping_address.line_1'] = 'Please enter the street address.';

  if (!address.city?.trim()) {
    errors['shipping_address.city'] = 'Please enter a town or city.';
  } else if (address.city.trim().length < 2) {
    errors['shipping_address.city'] = 'Please enter a full town or city name.';
  }

  if (!address.country_code) {
    errors['shipping_address.country_code'] = 'Please select a delivery country.';
  }

  /* Region: required only where the country actually uses one. */
  if (rules.regionRequired && !address.region?.trim()) {
    errors['shipping_address.region'] = `Please select a ${(rules.regionLabel ?? 'region').toLowerCase()}.`;
  }

  /* Postcode: label, requirement and format all follow the country. */
  const postcode = address.postcode?.trim() ?? '';

  if (rules.postcodeLabel) {
    if (rules.postcodeRequired && !postcode) {
      errors['shipping_address.postcode'] = `Please enter a ${rules.postcodeLabel.toLowerCase()}.`;
    } else if (postcode && rules.postcodePattern && !rules.postcodePattern.test(postcode)) {
      errors['shipping_address.postcode'] = rules.postcodeExample
        ? `That does not look like a ${rules.postcodeLabel.toLowerCase()} — for example, ${rules.postcodeExample}.`
        : `Please check the ${rules.postcodeLabel.toLowerCase()}.`;
    }
  }

  /* ------------------------------------------------------------ delivery */
  if (!method) errors['shipping_method'] = 'Please choose a delivery method.';

  if (method === 'named_day' && !scheduledDate) {
    errors['scheduled_delivery_date'] = 'Please choose a delivery date for named-day delivery.';
  }

  if (scheduledDate) {
    // Compared as dates, not timestamps, so "today" is not rejected by hours.
    const chosen = new Date(`${scheduledDate}T00:00:00`);
    const earliest = new Date();
    earliest.setHours(0, 0, 0, 0);
    earliest.setDate(earliest.getDate() + 1);

    if (Number.isNaN(chosen.getTime())) {
      errors['scheduled_delivery_date'] = 'Please enter a valid date.';
    } else if (chosen < earliest) {
      errors['scheduled_delivery_date'] = 'Please choose a date from tomorrow onwards.';
    }
  }

  if (!acceptsTerms) {
    errors['accepts_terms'] = 'Please accept the Terms & Conditions to place your order.';
  }

  return errors;
}
